import { createStore } from "vuex";
import axios from "axios";

// Create an axios instance with default headers
const apiClient = axios.create({
  baseURL: "/tpstock",
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add a response interceptor for handling 401/403 errors
apiClient.interceptors.response.use(
  (response) => response, // pass through successful responses
  (error) => {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      // Optionally clear token or do other cleanup
      localStorage.removeItem("token");
      // Redirect to login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export default createStore({
  state: {
    token: localStorage.getItem("token") || null,
    user: null,
    isAuthenticated: !!localStorage.getItem("token"),
    stocks: [],
  },
  mutations: {
    setAuth(state, { token, user }) {
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      localStorage.setItem("token", token);
    },
    clearAuth(state) {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem("token");
    },
  },
  actions: {
    async addStock(context, payload) {
      return await context.dispatch("stockOp", { ...payload, method: "post" });
    },
    async checkAvailable(context, { field, value }) {
      try {
        const res = await apiClient.get(
          "/check_" + field + "?" + field + "=" + value
        );
        if (res.status === 204) return true;
      } catch (e) {
        console.error(e);
      }

      return false;
    },
    async login({ commit }, { account, password }) {
      try {
        const response = await apiClient.post("/login", {
          account,
          password,
        });
        const { token, user } = response.data;
        commit("setAuth", { token, user });
        return true;
      } catch (error) {
        console.error("Login failed:", error.message, error.response?.data);
        return false;
      }
    },
    async logout({ commit }) {
      try {
        await apiClient.post("/logout");
        commit("clearAuth");
      } catch (error) {
        console.error("Logout failed:", error.message, error.response?.data);
      }
    },
    async fetchStocks() {
      const response = await apiClient.get("/stocks");
      return response.data;
    },
    async forgetPassword(context, { account, email }) {
      try {
        const res = await apiClient.post("/forget_password", {
          account,
          email,
        });
        if (res.status === 204) {
          return true;
        }

        console.warn("Unexpected response:", res.status, res.data);
      } catch (e) {
        console.error(e);
      }

      return false;
    },
    async postRegistration(context, { account, email, password }) {
      try {
        const res = await apiClient.post("/registration", {
          account,
          email,
          password,
        });

        if (res.status === 204) return true;
        console.warn("Unexpected response:", res.status, res.data);
      } catch (e) {
        console.error(e);
      }

      return false;
    },
    async removeStock(context, payload) {
      return await context.dispatch("stockOp", {
        ...payload,
        method: "delete",
      });
    },
    async resetPassword(context, { password, token }) {
      try {
        const res = await apiClient.post("/reset_password", {
          password,
          token,
        });
        if (res.status === 204) {
          return true;
        }
        console.warn("Unexpected response:", res.status, res.data);
      } catch (e) {
        console.error(e);
      }

      return false;
    },
    async stockOp(context, { stocks, method }) {
      try {
        const config = {
          url: `/stocks`,
          method,
          ...(method === "post" ? { data: { stocks } } : { data: { stocks } }),
        };

        const response = await apiClient(config);

        if (response.status !== 204) {
          console.warn("Unexpected response:", response.status, response.data);
          throw new Error(`Unexpected response status: ${response.status}`);
        }
      } catch (error) {
        console.error(
          `Stock ${method.toUpperCase()} failed:`,
          error.message,
          error.response?.data
        );
        throw error;
      }
    },
  },
  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    user: (state) => state.user,
  },
});
